/*
  Wishlist hearts outside the listing cards, all reading and writing through
  MNWishlistStore (assets/wishlist-store.js):

  <wishlist-button>        PDP heart (main-product, main-supplement, and the
                           hydration / supplement galleries). Follows the size
                           selected in the product form: filled when that size
                           is saved, and a tap saves or removes it. With no size
                           picked yet it shows whether any size is saved, and a
                           tap asks for a size, as Add to cart does. On a supply
                           buy box with flavours (Hydration Sticks) it follows
                           the flavours picked instead, see flavourLines().
  <cart-wishlist-button>   "Move to wishlist" on a cart drawer line: saves the
                           line's variant, then takes the line out of the cart.
  <drawer-wishlist-button> Heart in the supplement quick-add drawer
                           (component-product-quick-add-drawer.js setProduct()).

  The PDP colour switch (pdp-color-switch.js swapBuyButtons) replaces the PDP
  heart with a fresh element, which reconnects with the new product.

  Loaded per section, so every definition is guarded.
*/

// The product handle from a product URL (absolute or not, ?variant= or not).
function wishlistHandleFromUrl(url) {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return decodeURIComponent((path.split('/products/')[1] || '').split('/')[0]);
  } catch (e) {
    return '';
  }
}

// Calls back with the store once wishlist-store.js has run.
function whenWishlistStore(callback) {
  if (window.MNWishlistStore) {
    callback(window.MNWishlistStore);
  } else if (window.MNWishlistEnabled) {
    document.addEventListener('mn:wishlist-store', () => callback(window.MNWishlistStore), { once: true });
  }
}

if (!customElements.get('wishlist-button')) {
class WishlistButton extends HTMLElement {
    constructor() {
      super();

      this.button = this.querySelector("button");
      this.formId = this.dataset.formId;
      this.productId = Number(this.dataset.productId);
      this.handle = wishlistHandleFromUrl(this.dataset.productUrl);

      this.clickHandler = this.onClick.bind(this);
      this.render = this.render.bind(this);
      this.onFormChange = (event) => {
        if (event.target.closest && event.target.closest(`[id='${this.formId}'], [form='${this.formId}']`)) {
          // The form's id input updates after the option inputs change.
          setTimeout(this.render);
        }
      };
    }

    connectedCallback() {
      whenWishlistStore((store) => {
        if (!this.isConnected) return;
        this.store = store;
        this.button.classList.remove("loading");
        this.button.removeAttribute('disabled');
        this.button.setAttribute("aria-disabled", "false");
        this.button.addEventListener("click", this.clickHandler);
        this.unsubscribe = store.subscribe(this.render);
        document.addEventListener('variantChange', this.render);
        document.addEventListener('change', this.onFormChange);
        document.addEventListener('supply-buybox:flavours-change', this.render);
        this.render();
        store.ready().then(this.render);
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      this.button.removeEventListener("click", this.clickHandler);
      document.removeEventListener('variantChange', this.render);
      document.removeEventListener('change', this.onFormChange);
      document.removeEventListener('supply-buybox:flavours-change', this.render);
    }

    selectedVariantId() {
      const form = document.querySelector(`[id='${this.formId}']`);
      const value = form && new FormData(form).get('id');
      return value ? Number(value) : null;
    }

    // This product's variants, from the variants JSON inside the element
    // (main-product-add-to-cart.liquid). Empty where the markup has none.
    variants() {
      if (!this._variants) {
        let variants = [];
        try {
          const script = this.querySelector('script[type="application/json"]');
          variants = script ? JSON.parse(script.textContent) : [];
        } catch (e) {
          // Unreadable: match on the product only.
        }
        this._variants = Array.isArray(variants) ? variants : [];
      }
      return this._variants;
    }

    variantIds() {
      if (!this._variantIds) this._variantIds = new Set(this.variants().map((variant) => Number(variant.id)));
      return this._variantIds;
    }

    // Hydration Sticks sells through a supply buy box with a flavour picker
    // (snippets/hydration-supply-buybox.liquid), and its form's id input is
    // only a fallback flavour. So the heart saves the flavours picked there,
    // each against its own flavour product, as the listing cards save them.
    // With none picked it saves the product the page is for (the container,
    // or the flavour whose page it is), rather than asking for a flavour from
    // a heart that sits far above the picker on a phone.
    // Returns [{ productHandle, productId, variantId, ownerKnown }], or null
    // where the form has no flavour picker (Greens Gummies, every other PDP).
    flavourLines() {
      const form = document.querySelector(`[id='${this.formId}']`);
      const buybox = form && form.closest('.supply-buybox');
      const picker = buybox && buybox.querySelector('supply-buybox-flavours');
      if (!picker) return null;

      const picked = Array.from(picker.querySelectorAll('[data-flavour-id][aria-pressed="true"]'))
        .filter((tile) => tile.dataset.variantId)
        .map((tile) => ({
          productHandle: tile.dataset.flavourHandle,
          productId: Number(tile.dataset.flavourId),
          variantId: Number(tile.dataset.variantId),
          ownerKnown: true,
        }));
      if (picked.length) return picked;

      const variants = this.variants();
      const own = variants.find((variant) => variant.available) || variants[0];
      return own
        ? [{ productHandle: this.handle, productId: this.productId, variantId: Number(own.id), ownerKnown: true }]
        : [];
    }

    isSaved() {
      if (!this.store) return false;
      const flavourLines = this.flavourLines();
      if (flavourLines) {
        return flavourLines.length > 0 && flavourLines.every((line) => this.store.has(null, line.variantId));
      }
      const variantId = this.selectedVariantId();
      // No size picked yet: show whether any size of this product is saved. A
      // combined-listing parent's sizes are saved against its children
      // (wishlist-store.js ownerOf), so they're matched by variant as well.
      if (!variantId || !this.areRequiredVariantOptionsSelected()) {
        const variantIds = this.variantIds();
        return this.store
          .items()
          .some(
            (item) => item.handle === this.handle || item.productId === this.productId || variantIds.has(item.variantId)
          );
      }
      return this.store.has(null, variantId);
    }

    render() {
      const saved = this.isSaved();
      this.button.classList.toggle("added", saved);
      this.button.setAttribute(
        "aria-label",
        saved
          ? (window.variantStrings && window.variantStrings.removeFromWishlist) || 'Remove from wishlist'
          : (window.variantStrings && window.variantStrings.addToWishlist) || 'Add to wishlist'
      );
    }

  async onClick(event) {
    event.preventDefault();
    if (!this.store || this.button.classList.contains('loading')) return;

    const flavourLines = this.flavourLines();
    if (flavourLines) {
      await this.toggle(flavourLines);
      return;
    }

    const form = document.querySelector(`[id='${this.formId}']`);

    // Check if variant is selected before proceeding
    if (!this.areRequiredVariantOptionsSelected()) {
      // Add submitted class to trigger validation UI
      document.querySelectorAll(`[id='${this.formId}'], [form='${this.formId}']`).forEach((element) => {
        element.classList.add('submitted');
      });
      form.classList.add('submitted');

      // Show error message
      const errorMessage = this.getVariantSelectionMessage();
      this.showErrorMessage(errorMessage);
      return;
    }

    const variantId = this.selectedVariantId();
    if (!variantId) return;

    await this.toggle([{ productHandle: this.handle, productId: this.productId, variantId }]);
  }

  // Removes the lines when every one is saved, else saves the ones that aren't.
  async toggle(lines) {
    if (!lines.length) return;
    const savedItems = lines.map((line) => this.store.findItem(null, line.variantId));
    // Saved a moment ago and still in flight: nothing to remove yet.
    if (lines.some((line, index) => !savedItems[index] && this.store.has(null, line.variantId))) return;
    const removing = savedItems.every(Boolean);

    this.button.classList.add('loading');
    try {
      if (removing) {
        await Promise.all(savedItems.map((item) => this.store.remove(item.itemId)));
      } else {
        await Promise.all(lines.filter((line, index) => !savedItems[index]).map((line) => this.store.add(line)));
      }
    } catch (error) {
      console.error('Wishlist: PDP heart failed', error);
      this.showErrorMessage(
        removing
          ? "Couldn't remove this from your wishlist. Please try again."
          : "Couldn't save this to your wishlist. Please try again."
      );
    } finally {
      this.button.classList.remove('loading');
      this.render();
    }
  }

  areRequiredVariantOptionsSelected() {
    // Check if we're on a product page with variants
    if (!document.body.classList.contains('template-product')) {
      return true;
    }

    // Find variant selectors to check if options are selected
    const productInfoContainer = document.querySelector('.product__info-container');
    const variantRadios = productInfoContainer ? productInfoContainer.querySelector('variant-radios') : document.querySelector('variant-radios');
    const variantSelects = productInfoContainer ? productInfoContainer.querySelector('variant-selects') : document.querySelector('variant-selects');

    if (variantRadios) {
      // For radio button variant pickers - check visible fieldsets
      const fieldsets = variantRadios.querySelectorAll('fieldset:not(.hidden)');

      for (let fieldset of fieldsets) {
        // Check if this fieldset has any radio buttons with checked state
        const hasCheckedRadio = fieldset.querySelector('input:checked');
        if (!hasCheckedRadio) {
          return false;
        }
      }
    }

    if (variantSelects) {
      // For dropdown variant pickers
      const selects = variantSelects.querySelectorAll('select');

      for (let select of selects) {
        if (!select.value) {
          return false;
        }
      }
    }

    return true;
  }

  getVariantSelectionMessage() {
    // Find variant selectors to determine what options need to be selected
    const productInfoContainer = document.querySelector('.product__info-container');
    const variantRadios = productInfoContainer ? productInfoContainer.querySelector('variant-radios') : document.querySelector('variant-radios');
    const variantSelects = productInfoContainer ? productInfoContainer.querySelector('variant-selects') : document.querySelector('variant-selects');

    let unselectedOptions = [];

    if (variantRadios) {
      // For radio button variant pickers - check visible fieldsets
      const fieldsets = variantRadios.querySelectorAll('fieldset:not(.hidden)');

      fieldsets.forEach(fieldset => {
        const legend = fieldset.querySelector('legend span');
        const hasCheckedRadio = fieldset.querySelector('input:checked');

        if (!hasCheckedRadio && legend) {
          // Only the span's own text: the Size legend also nests the
          // model-fit line.
          const name = Array.from(legend.childNodes)
            .filter((node) => node.nodeType === Node.TEXT_NODE)
            .map((node) => node.textContent)
            .join('')
            .trim();
          if (name) unselectedOptions.push(name);
        }
      });
    }

    if (variantSelects) {
      // For dropdown variant pickers
      const selects = variantSelects.querySelectorAll('select');

      selects.forEach(select => {
        const label = variantSelects.querySelector(`label[for="${select.id}"]`);

        if (!select.value && label) {
          unselectedOptions.push(label.textContent.trim());
        }
      });
    }

    if (unselectedOptions.length > 0) {
      return `Please select a ${unselectedOptions.join(', ')}`;
    }

    return 'Please select all product options';
  }

  showErrorMessage(message) {
    // Find or create error message wrapper
    const form = document.querySelector(`[id='${this.formId}']`);
    // supply-buybox-form: the Hydration Sticks / Greens Gummies buy box.
    const productForm = form && form.closest('product-form, supply-buybox-form');

    if (!productForm) return;

    let errorWrapper = productForm.querySelector('.product-form__error-message-wrapper, .supply-buybox__error-message-wrapper');

    if (!errorWrapper) return;

    let errorMessage = errorWrapper.querySelector('.product-form__error-message, .supply-buybox__error-message');

    if (errorMessage) {
      errorMessage.textContent = message;
      errorWrapper.removeAttribute('hidden');

      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        errorWrapper.setAttribute('hidden', '');
      }, 5000);
    }
  }
}

customElements.define("wishlist-button", WishlistButton);
}

if (!customElements.get('cart-wishlist-button')) {
class CartWishlistButton extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('button');
    this.productId = Number(this.dataset.productId);
    this.variantId = Number(this.dataset.variantId);
    this.lineKey = this.dataset.lineKey;
    this.handle = wishlistHandleFromUrl(this.dataset.productUrl);

    this.clickHandler = this.onClick.bind(this);
  }

  // The cart drawer re-renders its lines on every cart change, so each line's
  // button connects fresh and reads the store's copy (no wishlist request).
  connectedCallback() {
    whenWishlistStore((store) => {
      if (!this.isConnected) return;
      this.store = store;
      this.button.classList.remove('loading');
      this.button.removeAttribute('disabled');
      this.button.setAttribute('aria-disabled', 'false');
      this.button.addEventListener('click', this.clickHandler);
    });
  }

  disconnectedCallback() {
    this.button.removeEventListener('click', this.clickHandler);
  }

  async onClick(event) {
    event.preventDefault();
    if (!this.store || !this.variantId) return;

    // The button says what it does, and the item is one tap from the cart
    // again in the wishlist drawer, so there's no confirm box. A failure shows
    // in the line's own error slot, like a quantity error.
    this.showError('');
    setCartDrawerLoading(true);
    try {
      // Already saved (this size): nothing to save, just take it out of the cart.
      // A cart line's product (item.product.id) is the one that owns its
      // variant, so the store has nothing to look up.
      if (!this.store.has(null, this.variantId)) {
        await this.store.add({
          productHandle: this.handle,
          productId: this.productId,
          variantId: this.variantId,
          ownerKnown: true,
        });
      }
      this.button.classList.add('added');
    } catch (e) {
      console.error('Failed to move cart line to wishlist', e);
      setCartDrawerLoading(false);
      this.showError("Couldn't save this to your wishlist, so it's still in your cart. Please try again.");
      return;
    }

    try {
      if (!this.lineKey) return;

      const cart = await this.removeLine();
      if (typeof window.handleCartUpdated === 'function') {
        window.handleCartUpdated(cart);
      } else {
        window.cartJSON = cart;
      }

      // Drawn only now, from the cart as it ended up.
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        const data = await fetch('/?sections=cart-drawer,cart-icon-bubble', {
          method: 'GET',
        }).then((r) => r.json());
        cartDrawer.renderContents({ sections: data });
      }
    } catch (e) {
      console.error('Failed to remove cart line after moving to wishlist', e);
      this.showError("Saved to your wishlist, but it couldn't be taken out of your cart. Please remove it here.");
    } finally {
      setCartDrawerLoading(false);
    }
  }

  // Takes the line out of the cart and checks it stays out. Another write in
  // flight alongside the change, such as the _swish attribute Swish's checkout
  // bridge writes when this save was a guest's first (global.js,
  // cartWritesSettled), can put the line back after the change has answered
  // without it. So the change queues behind the theme's own cart writes and
  // waits for anyone else's, then the cart is read back once every write has
  // landed, and a line that came back is removed again. Resolves with that
  // cart.
  async removeLine() {
    const settled = () => (window.cartWritesSettled ? window.cartWritesSettled() : Promise.resolve());
    const body = JSON.stringify({ id: this.lineKey, quantity: 0 });
    const attempt = async () => {
      await settled();
      try {
        await window.fetchCartJSON(`${routes.cart_change_url}`, { ...window.fetchConfig(), body });
      } catch (e) {
        // A throttled or dropped change is settled by the read below.
        console.warn('Move to wishlist: cart change failed', e);
      }
      await settled();
      const { json } = await window.fetchCartJSON('/cart.js', { method: 'GET' });
      return json;
    };

    for (let tries = 0; tries < 3; tries++) {
      const cart = await (window.serializeCartAdd ? window.serializeCartAdd(attempt) : attempt());
      if (!cart || !Array.isArray(cart.items)) throw new Error('Unreadable cart');
      if (!cart.items.some((item) => item.key === this.lineKey)) return cart;
    }
    throw new Error('Line still in the cart');
  }

  // The line's error slot in snippets/cart-drawer.liquid (.cart-item__error-text),
  // the same one quantity errors use.
  showError(message) {
    const errorText = this.closest('.cart-item')?.querySelector('.cart-item__error-text');
    if (errorText) errorText.textContent = message;
  }
}

customElements.define('cart-wishlist-button', CartWishlistButton);
}

if (!customElements.get('drawer-wishlist-button')) {
class DrawerWishlistButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this._productData = null;
    this.clickHandler = this.onClick.bind(this);
    this.render = this.render.bind(this);
  }

  connectedCallback() {
    whenWishlistStore((store) => {
      this.store = store;
      if (!this.unsubscribe) this.unsubscribe = store.subscribe(this.render);
      this.render();
    });
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    this.unsubscribe = null;
  }

  // Called by the quick-add drawer on open and whenever the flavour changes.
  setProduct({ productId, productTitle, productUrl, imageUrl, variant }) {
    this._productData = {
      productId,
      productTitle,
      productUrl,
      imageUrl,
      variant,
      handle: wishlistHandleFromUrl(productUrl),
    };
    this.button.removeEventListener('click', this.clickHandler);
    this.button.addEventListener('click', this.clickHandler);
    this.render();
  }

  reset() {
    this.button.classList.add('loading');
    this.button.classList.remove('added');
    this.button.setAttribute('aria-disabled', 'true');
    this.button.removeEventListener('click', this.clickHandler);
    this._productData = null;
  }

  render() {
    if (!this._productData || !this.store) return;
    const { variant } = this._productData;
    this.button.classList.remove('loading');
    this.button.setAttribute('aria-disabled', 'false');
    this.button.classList.toggle('added', Boolean(variant) && this.store.has(null, variant.id));
  }

  async onClick(event) {
    event.preventDefault();
    if (!this._productData || !this.store || this.button.classList.contains('adding')) return;
    const { productId, handle, variant } = this._productData;
    if (!variant) return;

    const savedItem = this.store.findItem(null, variant.id);
    if (!savedItem && this.store.has(null, variant.id)) return;

    this.button.classList.add('adding');
    try {
      // The 700ms floor keeps the save animation readable on a fast response.
      await Promise.all([
        savedItem
          ? this.store.remove(savedItem.itemId)
          : this.store.add({ productHandle: handle, productId, variantId: variant.id }),
        new Promise((resolve) => setTimeout(resolve, 700)),
      ]);
    } catch (error) {
      console.error('Wishlist: quick-add heart failed', error);
    } finally {
      this.button.classList.remove('adding');
      this.render();
    }
  }
}

customElements.define('drawer-wishlist-button', DrawerWishlistButton);
}
